import React, { useState } from 'react'

import { PremiumBadge } from '../PremiumBadge'
import { Button } from '../Button'
import { MinimalButton } from '../MinimalButton'
import { ProgressBar } from '../ProgressBar'
import { translated } from '../_translation'
import { copyToClipboard } from '../../js/utility'
import { PremiumBadgeShadow, Avatar1, Avatar2 } from './svgs.jsx'

import './InviteWidget.scss'

/**
 * @typedef {Object} InviteWidgetPropsType
 * @property {Object} props
 * @property {string} [props.className] Additional classes
 * @property {boolean} loggedIn Whether the user is logged in
 * @property {'free'|'subscribed'} [premiumType] The premium flavor of the user, if any
 * @property {string} [props.userAvatar] The user's avatar
 * @property {string} props.inviteLink The invite-link
 * @property {string} props.loginHref The login-link
 * @property {string} [props.facebookHref] Link for the Facebook-button
 * @property {string} [props.twitterHref] Link for the Twitter-button
 * @property {string} props.subscribeHref Link for the Subscribe-button
 * @property {number} [props.daysLeft] How many days of premium a free-premium user has left
 * @property {number} [props.maxDaysLeft] Max number of free premium days
 */

/**
 * InviteWidget used on various places (e.g. home, invite-friends),
 * with different authentication states.
 * @param {InviteWidgetPropsType} props
 */
const InviteWidget = ({
    gettext,
    className,
    loggedIn,
    premiumType,
    userAvatar,
    inviteLink,
    loginHref,
    facebookHref,
    twitterHref,
    subscribeHref,
    daysLeft,
    maxDaysLeft,
}) => {
    const strings = {
        userAvatar: gettext('User Avatar'),
        premiumDaysLabel: gettext('Premium Days'),
        dayLeftLabel: gettext('day left'),
        daysLeftLabel: gettext('days left'),
        maxDaysLabel: gettext('of {days} days').replace('{days}', maxDaysLeft),
        signUpButton: gettext('Sign Up or Log in'),
        loginMessage: gettext('Log in to view and share your personal invitation link.'),
        premiumLoop: gettext('Stay in the Premium Loop:'),
        inviteMore: gettext('Invite more friends:'),
        inviteWithLink: gettext('Invite friends with your personal link:'),
        subscribeButton: gettext('Subscribe'),
        copiedText: gettext('Copied to Clipboard!'),
        facebook: gettext('Facebook'),
        facebookTooltip: gettext('Share on Facebook'),
        twitter: gettext('Twitter'),
        twitterTooltip: gettext('Share on Twitter'),
        email: gettext('Mail'),
        emailTooltip: gettext('Send via email'),
        copyLink: gettext('Copy Link'),
        inviteLink: gettext('Invite Link'),
        // TODO: WEB-694 Update emailSubject with real value
        emailSubject: gettext('Join me on Bikemap and earn Premium'),
        shareBody: gettext('Cycling is more fun together. Join me on Bikemap!'),
    }

    let classes = 'invite-widget ' + (premiumType || '')
    if (className) classes += ' ' + className

    const overviewProps = {
        premiumType,
        userAvatar,
        daysLeft,
        maxDaysLeft,
        strings,
    }

    const messageProps = {
        loggedIn,
        premiumType,
        subscribeHref,
        strings,
    }

    const inviteProps = {
        loggedIn,
        premiumType,
        inviteLink,
        facebookHref,
        twitterHref,
        loginHref,
        strings,
    }

    return (
        <div className={classes}>
            <InviteWidgetOverview {...overviewProps} />
            {(!loggedIn || premiumType === 'free') && <InviteWidgetMessage {...messageProps} />}
            <InviteWidgetButtons {...inviteProps} />
        </div>
    )
}

export default translated(InviteWidget)

/**
 * Displays the top-part of the invite widget
 * @param {Object} props
 * @param {'free'|'subscribed'} [premiumType]
 * @param {string} [props.userAvatar]
 * @param {number} [props.daysLeft]
 * @param {number} [props.maxDaysLeft]
 * @param {Object} props.strings
 */
const InviteWidgetOverview = ({
    premiumType,
    userAvatar,
    daysLeft,
    maxDaysLeft,
    strings,
}) => {
    const daysLeftRelative = daysLeft > 0 && maxDaysLeft > 0 ? (daysLeft / maxDaysLeft) : 0
    return (
        <div className="overview">
            <div className="avatar">
                {userAvatar ? (
                    <img src={userAvatar} alt={strings.userAvatar} className="avatar-image" loading='lazy' />
                ) : (
                    premiumType === 'free' ? (
                        <i className='i-helmet text-secondary' />
                    ) : <Avatar1 />
                )}
                {premiumType === undefined
                    ? <PremiumBadgeShadow />
                    : <PremiumBadge data-testid="premium-badge" />
                }
            </div>
            {premiumType === 'free' ? (
                <div className="countdown">
                    <span>{strings.premiumDaysLabel}</span>
                    <ProgressBar progress={daysLeftRelative} className="progress-bar" />
                    <div className="labels">
                        <span>{daysLeft + ' ' + (daysLeft === 1 ? strings.dayLeftLabel : strings.daysLeftLabel)}</span>
                        <span>{strings.maxDaysLabel}</span>
                    </div>
                </div>
            ) : (
                <div className="avatar">
                    <Avatar2 />
                    <PremiumBadgeShadow />
                </div>
            )}
        </div>
    )
}

/**
 * Displays the middle-part of the invite widget
 * @param {Object} props
 * @param {boolean} loggedIn
 * @param {'free'|'subscribed'} [premiumType]
 * @param {string} props.subscribeHref Link for the Subscribe-button
 * @param {Object} props.strings
 */
const InviteWidgetMessage = ({
    loggedIn,
    premiumType,
    subscribeHref,
    strings,
}) => {
    return (
        <div className={'content ' + (!loggedIn && 'text-center')}>
            <h4 className="content-label">
                {premiumType === 'free' ? strings.premiumLoop : strings.loginMessage}
            </h4>
            {premiumType === 'free' &&
                <Button href={subscribeHref} variant="premium" size="lg">
                    {strings.subscribeButton}
                </Button>
            }
        </div>
    )
}

/**
 * Displays the bottom-part of the invite widget
 * @param {Object} props
 * @param {boolean} loggedIn Whether the user is logged in
 * @param {'free'|'subscribed'} [premiumType] The premium flavor of the user, if any
 * @param {string} props.inviteLink The invite-link
 * @param {string} props.loginHref The login-link
 * @param {string} [props.facebookHref] Link for the Facebook-button
 * @param {string} [props.twitterHref] Link for the Twitter-button
 * @param {Object} props.strings
 */
const InviteWidgetButtons = ({
    loggedIn,
    premiumType,
    inviteLink,
    loginHref,
    facebookHref,
    twitterHref,
    strings,
}) => {

    const openShareWindow = (e, url, name) => {
        e.preventDefault()
        const width = 575
        const height = 650
        const top = window.top.outerHeight / 2 + window.top.screenY - (height / 2)
        const left = window.top.outerWidth / 2 + window.top.screenX - (width / 2)

        window.open(
            url,
            name,
            'toolbar=no, location=no, menubar=no, copyhistory=no' +
            ', width=' + width + ', height=' + height + ', top=' + top + ', left=' + left
        )
    }

    const [isCopied, setIsCopied] = useState(false)

    /**
     * Callback that copies the invite-href to the clipboard
     */
    const copyInviteLink = async () => {
        await copyToClipboard(inviteLink)

        setIsCopied(true)
        setTimeout(() => {
            setIsCopied(false)
        }, 2500)
    }

    const facebookUrl = inviteLink && facebookHref
        ? facebookHref
            .replace('{shareUrlParam}', encodeURI(inviteLink)) : ''

    const twitterUrl = inviteLink && twitterHref
        ? twitterHref
            .replace('{shareUrlParam}', encodeURI(inviteLink))
            .replace('{shareTextParam}', encodeURI(strings.shareBody)) : ''

    const shareBody = inviteLink
        ? encodeURI(strings.shareBody) + '%0D%0A%0D%0A' + encodeURI(inviteLink) : ''

    return (
        <div className="invite-buttons">
            {loggedIn ? (
                <>
                    <h5 className="invite-label">
                        {premiumType === 'free' ? strings.inviteMore : strings.inviteWithLink}
                    </h5>
                    <div className="invite-link">
                        <input
                            className={'invite-link-text'}
                            value={inviteLink}
                            readOnly
                            aria-label={strings.inviteLink}
                        />
                        <div className={'invite-link-copied' + (!isCopied ? ' is-hidden' : '')}>
                            {strings.copiedText}
                        </div>
                        {isCopied ? (
                            <MinimalButton
                                className="invite-link-button"
                                icon="i-check"
                                aria-label={strings.copiedText}
                            />
                        ) : (
                            <MinimalButton
                                className="invite-link-button"
                                icon="i-copy"
                                data-testid="copy-button"
                                onClick={copyInviteLink}
                                aria-label={strings.copyLink}
                            />
                        )}
                    </div>
                    <div className="buttons">
                        {facebookUrl && (
                            <Button
                                className="bm-tooltip-top-left"
                                data-tooltip={strings.facebookTooltip}
                                aria-label={strings.facebookTooltip}
                                href="#"
                                target="_blank"
                                rel="noopener nofollow"
                                onClick={(e) => openShareWindow(e, facebookUrl, 'fbwindow')}
                            >
                                <i className="i-facebook" />
                                <span>{strings.facebook}</span>
                            </Button>
                        )}
                        {twitterUrl && (
                            <Button
                                className="bm-tooltip-top-left"
                                data-tooltip={strings.twitterTooltip}
                                aria-label={strings.twitterTooltip}
                                href="#"
                                target="_blank"
                                rel="noopener nofollow"
                                onClick={(e) => openShareWindow(e, twitterUrl, 'twitterwindow')}
                            >
                                <i className="i-twitter" />
                                <span>{strings.twitter}</span>
                            </Button>
                        )}
                        {shareBody && (
                            <Button
                                className="bm-tooltip-top-right"
                                data-tooltip={strings.emailTooltip}
                                aria-label={strings.emailTooltip}
                                target="_blank"
                                rel="nofollow"
                                href={'mailto:?subject=' + strings.emailSubject + '&body=' + shareBody}
                            >
                                <i className="i-email" />
                                <span>{strings.email}</span>
                            </Button>
                        )}
                    </div>
                </>
            ) : (
                <div className="buttons">
                    <Button href={loginHref} variant="primary" size="lg">
                        {strings.signUpButton}
                    </Button>
                </div>
            )}
        </div>
    )
}
